import { useGetLocale, useGo, useParsed, useTranslate } from '@refinedev/core';
import { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import useWorkspace from '@components/hooks/useWorkspace';
import ListPage from '@components/pages/list';
import useProject from '@components/hooks/useProject';
import { GetStaticProps, GetStaticPaths } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import parseISO from 'date-fns/parseISO';

interface DocumentInfo {
  id: string;
  title: string;
}

export default function DocumentList() {
  const [workspaceId] = useWorkspace();
  const [projectId] = useProject();
  const gotTo = useGo();
  const t = useTranslate();
  const columnDefs: ColumnDef<DocumentInfo>[] = useMemo(() => {
    return [
      {
        id: 'title',
        header: t('app:list.headers.title'),
        accessorKey: 'title',
      },
      {
        id: 'id',
        header: t('app:list.headers.id'),
        accessorKey: 'id',
      },
      {
        id: 'updatedAt',
        header: t('app:list.headers.updated_at'),
        accessorKey: '$updatedAt',
        cell: (p) => {
          return formatDistanceToNow(parseISO(p.getValue() as string), {
            addSuffix: true,
          });
        },
      },
    ];
  }, [t]);
  return (
    <ListPage
      title="app:list.document_title"
      description="app:list.document_description"
      columns={columnDefs}
      onRowClick={(documentId: string) => {
        gotTo({
          to: `/${workspaceId}/${projectId}/${documentId}`,
          type: 'push',
        });
      }}
      refineCoreProps={{
        resource: process.env.NEXT_PUBLIC_RID_DOCUMENT,
        queryOptions: {
          enabled: !!workspaceId && !!projectId,
        },
        filters: {
          permanent: [
            {
              field: 'workspaceId',
              operator: 'eq',
              value: workspaceId,
            },
            {
              field: 'projectId',
              operator: 'eq',
              value: projectId,
            },
          ],
        },
      }}
    />
  );
}

DocumentList.layout = 'app';

export const getStaticProps: GetStaticProps = async (context) => {
  const i18nProps = await serverSideTranslations(context.locale ?? 'en', [
    'common',
    'app',
  ]);

  return {
    props: {
      ...i18nProps,
    },
  };
};

export const getStaticPaths: GetStaticPaths = (context) => {
  return {
    paths: [],
    fallback: true,
  };
};
